import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import React, { SyntheticEvent, useEffect, useState, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import {
  getClientSalesmenMetrics,
  getRankByEmail,
  getRankByStableAccount,
  getRankByWgsDetails,
} from "../../../store/thunks/portalManagementThunk";
import {
  GetArray,
  GetObject,
  WithCommas,
  exportToExcel,
} from "../../../utils/utils";
import { BarChart } from "@mui/x-charts";
import MiniSpinner from "../../reusableComponents/miniSpinner/miniSpinner";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  Bar,
  Legend,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  Line,
} from "recharts";
import { MRT_ColumnDef, useMaterialReactTable } from "material-react-table";
import TableTemplate from "../../reusableComponents/tableTemplate/tableTemplate";
import { logoutUser } from "../../../store/thunks/authenticationThunk";
import { portalManagementReset } from "../../../store/features/portalManagement/portalManagementSlice";
import { systemManagementReset } from "../../../store/features/systemManagement/systemManagementSlice";
import { logout } from "../../../store/features/authentication/authenticationSlice";
import { useNavigate } from "react-router-dom";
import { tokens, eyebrowSx } from "../../../theme";
import SectionCard from "../../reusableComponents/sectionCard/sectionCard";
import StatTile from "../../reusableComponents/statTile/statTile";
import SegmentedControl from "../../reusableComponents/segmentedControl/segmentedControl";
import EmptyState from "../../reusableComponents/emptyState/emptyState";
import RankMiniList from "./rankMiniList";
import { buildRankEntries } from "./salesRepViewModel";

/** Tooltip formatter for the diverging gain/lost bars — Lost plots as negative
 *  values so it hangs below the axis, but readers should see the magnitude. */
const absCount = (v: number | null) => (v == null ? "" : `${Math.abs(v)}`);

/** % change vs the prior period; undefined (no chip) when there is no prior or
 *  the prior is zero — a tile without a delta beats an invented one. */
const pctChange = (curr: any, prev: any): number | undefined =>
  Number(prev) ? ((Number(curr) - Number(prev)) / Number(prev)) * 100 : undefined;

const RANKING_PANEL_WIDTH = 360;

/** The KPI strip sits directly on the canvas, so each tile gets the same card
 *  chrome as its SectionCard neighbours — tileBg grey is for tiles INSIDE a
 *  card and blends into the canvas out here (user feedback 2026-08-07). */
const KPI_CARD_SX = {
  backgroundColor: tokens.color.surface,
  border: `1px solid ${tokens.color.border}`,
  borderRadius: `${tokens.radius.card}px`,
  boxShadow: tokens.shadow.card,
} as const;

const ChartLoading = ({ height = 300 }: { height?: number }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height,
      width: "100%",
    }}
  >
    <MiniSpinner />
  </Box>
);

const SalesRepView = () => {
  const { user } = useAppSelector((state: any) => state.authentication);
  const {
    selectedClient,
    selectedSalesRep,
    salemRawData,
    salstbRawData,
    rankByStableAccountLoading,
    rankByEmailLoading,
    rankByWgsDetailsLoading,
    rankByWgsDetailsRawData,
    selectedLastUpdated,
    clientSalesmenData,
    clientSalesmenMetricsLoading,
  } = useAppSelector((state: any) => state.portalManagement);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [salemChartData, setSalemChartData] = useState([]);
  const [salemChartLabels, setSalemChartLabels] = useState([]);

  const [salstbChartData, setSalstbChartData] = useState([]);
  const [salstbChartLabels, setSalstbChartLabels] = useState([]);

  const [originalBranch, setOriginalBranch] = useState<any>("Overall");

  const [salesRepBranches, setSalesRepBranches] = useState<any>([]);
  const [salesRepList, setSalesRepList] = useState<any>([]);

  const [selectedSalesRepSalesRep, setSelectedSalesRepSalesRep] =
    useState<any>(null);
  const [selectedSalesRepBranch, setselectedSalesRepBranch] = useState<any>(null);

  const [selectedSalesRepRetLead, setselectedSalesRepRetLead] =
    useState<any>(null);

  const [gainLostTotalData, setGainLostTotalData] = useState<any>([]);

  const [retentionLeadTableData, setRetentionLeadTableData] = useState<any>([]);

  const [clientSalesmenChartYou, setClientSalesmenChartYou] = useState<any>([]);
  const [
    clientSalesmenChartMetricAverage,
    setClientSalesmenChartMetricAverage,
  ] = useState<any>([]);
  const [clientSalesmenChartLabel, setClientSalesmenChartLabel] = useState<any>(
    []
  );

  const [sgain, setSgain] = useState(0);
  const [slost, setSlost] = useState(0);
  const [sacc, setSacc] = useState(0);

  const retentionLeadOption: any[] = [
    { label: "Lead 9-12 Weeks", value: "Lead" },
    { label: "Lost Customers", value: "Lost" },
    { label: "Gained Customers", value: "Gain" },
    { label: "All Customers", value: "Last Purchase" },
  ];

  const [retLeadError, setRetLeadError] = useState(false);
  const [retLeadErrorMessage, setRetLeadErrorMessage] = useState("");

  useEffect(() => {
    if (selectedClient) {
      dispatch(
        getRankByStableAccount({
          customer_id: selectedClient.customer_id,
        })
      ).then((result: any) => {
        if (result?.error) {
          if (result?.error?.message === "Rejected") {
            dispatch(logoutUser({ user_index: user?.client_user_index }));
            dispatch(portalManagementReset());
            dispatch(systemManagementReset());
            dispatch(logout());
            navigate("/login");
          }
        }
      });
      dispatch(
        getRankByEmail({
          customer_id: selectedClient.customer_id,
        })
      );
      dispatch(
        getRankByWgsDetails({
          customer_id: selectedClient.customer_id,
        })
      );
      dispatch(
        getClientSalesmenMetrics({
          customer_id: selectedClient.customer_id,
          salesrep: selectedSalesRep?.SALESMAN,
        })
      );
    }
  }, [user, selectedClient, selectedSalesRep, dispatch,  navigate]);

  const salesRepFilterData = () => {
    if (salemRawData) {
      const salemFilter = GetObject(
        GetArray(salemRawData).filter((item: any) => {
          return (
            item.BRANCH ===
            (selectedSalesRepBranch
              ? selectedSalesRepBranch?.value
              : originalBranch)
          );
        })
      );

      if (Object.keys(salemFilter).length !== 0) {
        setSalemChartData(salemFilter.EMAIL_PERCENT);
        setSalemChartLabels(salemFilter.SALESMAN);
      } else {
        setSalemChartData([]);
        setSalemChartLabels([]);
      }
    }

    if (salstbRawData) {
      const salstbFilter = GetObject(
        GetArray(salstbRawData).filter((item: any) => {
          return (
            item.BRANCH ===
            (selectedSalesRepBranch
              ? selectedSalesRepBranch?.value
              : originalBranch)
          );
        })
      );
      if (Object.keys(salstbFilter).length !== 0) {
        setSalstbChartData(salstbFilter.STABLE_PERCENT);
        setSalstbChartLabels(salstbFilter.SALESMAN);
      } else {
        setSalstbChartData([]);
        setSalstbChartLabels([]);
      }
    }
  };

  const salesRepMetricsFilterData = () => {
    if (clientSalesmenData) {
      setClientSalesmenChartYou(
        clientSalesmenData?.salesman_rank_metric?.SALESMAN_LABEL
      );
      setClientSalesmenChartMetricAverage(
        clientSalesmenData?.salesman_rank_metric?.AVG_METRIC
      );
      setClientSalesmenChartLabel(
        clientSalesmenData?.salesman_rank_metric?.KEY_METRIC
      );
    } else {
      setClientSalesmenChartYou([]);
      setClientSalesmenChartMetricAverage([]);
      setClientSalesmenChartLabel([]);
    }
  };

  const handleBranchChange = (selection: any) => {
    let tempBranchSelection: string = "";
    setselectedSalesRepBranch(selection);

    if (selection) {
      tempBranchSelection = selection.value;
    } else {
      tempBranchSelection = originalBranch;
    }

    const tempSalesRepList: any[] = [];

    rankByWgsDetailsRawData?.unique_salesman?.BRANCH.forEach(
      (element: any, index: any) => {
        if (element === tempBranchSelection) {
          tempSalesRepList.push({
            label: rankByWgsDetailsRawData?.unique_salesman?.Salesman?.[index],
            value: rankByWgsDetailsRawData?.unique_salesman?.Salesman?.[index],
          });
        }
      }
    );

    setSalesRepList(tempSalesRepList);
    setSelectedSalesRepSalesRep(
      tempSalesRepList.length > 0 ? tempSalesRepList[0] : null
    );

    let tempSalesRep = "";

    if (tempSalesRepList.length > 0) {
      tempSalesRep = tempSalesRepList[0]?.value;
    } else {
      tempSalesRep = selectedSalesRep?.SALESMAN;
    }

    if (rankByWgsDetailsRawData?.salesman_12_metric) {
      const sal12Filter = GetObject(
        GetArray(rankByWgsDetailsRawData?.salesman_12_metric).filter(
          (item: any) =>
            item.BRANCH === tempBranchSelection &&
            item.SALESMAN === tempSalesRep
        )
      );

      const tempGainLostTotalData: any[] = [];
      if (sal12Filter.END_PERIOD) {
        sal12Filter?.END_PERIOD.forEach((element: any, index: any) => {
          tempGainLostTotalData.push({
            name: element,
            Gained: sal12Filter?.GAINED_ACCOUNT?.[index],
            Lost: sal12Filter?.LOST_ACCOUNT?.[index],
            Active: sal12Filter?.TOTAL_ACCOUNT?.[index],
          });
        });
      }

      setGainLostTotalData(tempGainLostTotalData);

      setSgain(
        sal12Filter.GAINED_ACCOUNT
          ? sal12Filter.GAINED_ACCOUNT[sal12Filter.GAINED_ACCOUNT.length - 1]
          : 0
      );
      setSlost(
        sal12Filter.LOST_ACCOUNT
          ? sal12Filter.LOST_ACCOUNT[sal12Filter.LOST_ACCOUNT.length - 1]
          : 0
      );
      setSacc(
        sal12Filter.TOTAL_ACCOUNT
          ? sal12Filter.TOTAL_ACCOUNT[sal12Filter.TOTAL_ACCOUNT.length - 1]
          : 0
      );
    }

    setselectedSalesRepRetLead(null);
    setRetentionLeadTableData([]);
  };
  const handleSalesRepChange = (selection: any) => {
    let tempSalesRepSelection: string = "";
    if (selection) {
      setSelectedSalesRepSalesRep(selection);
      tempSalesRepSelection = selection.value;
    } else {
      setSelectedSalesRepSalesRep(salesRepList[0]);
      tempSalesRepSelection = salesRepList[0].value;
    }

    if (rankByWgsDetailsRawData.salesman_12_metric) {
      const sal12Filter = GetObject(
        GetArray(rankByWgsDetailsRawData.salesman_12_metric).filter(
          (item: any) =>
            item.BRANCH === selectedSalesRepBranch.value &&
            item.SALESMAN === tempSalesRepSelection
        )
      );

      const tempGainLostTotalData: any[] = [];
      if (sal12Filter.END_PERIOD) {
        sal12Filter?.END_PERIOD.forEach((element: any, index: any) => {
          tempGainLostTotalData.push({
            name: element,
            Gained: sal12Filter?.GAINED_ACCOUNT?.[index],
            Lost: sal12Filter?.LOST_ACCOUNT?.[index],
            Active: sal12Filter?.TOTAL_ACCOUNT?.[index],
          });
        });
      }

      setGainLostTotalData(tempGainLostTotalData);

      setSgain(
        sal12Filter.GAINED_ACCOUNT
          ? sal12Filter.GAINED_ACCOUNT[sal12Filter.GAINED_ACCOUNT.length - 1]
          : 0
      );
      setSlost(
        sal12Filter.LOST_ACCOUNT
          ? sal12Filter.LOST_ACCOUNT[sal12Filter.LOST_ACCOUNT.length - 1]
          : 0
      );
      setSacc(
        sal12Filter.TOTAL_ACCOUNT
          ? sal12Filter.TOTAL_ACCOUNT[sal12Filter.TOTAL_ACCOUNT.length - 1]
          : 0
      );
    }

    setselectedSalesRepRetLead(null);
    setRetentionLeadTableData([]);
  };
  const handleRetentionLeadChange = (selection: any) => {
    setRetLeadError(false);
    setRetLeadErrorMessage("");
    if (!selectedSalesRepBranch || !selectedSalesRepSalesRep) {
      setRetLeadError(true);
      setRetLeadErrorMessage("Please select a branch and sales rep first");
      return;
    }

    setselectedSalesRepRetLead(selection);

    if (selection) {
      if (rankByWgsDetailsRawData?.salesman_lead) {
        setRetentionLeadTableData(
          rankByWgsDetailsRawData?.salesman_lead?.[selection?.value][
            selectedSalesRepBranch?.value
          ][selectedSalesRepSalesRep?.value]
        );
      }
    }
  };

  const salesRepFilterDetailData = () => {
    if (rankByWgsDetailsRawData) {
      const tempSalesRepBranches: any[] = [];
      const tempSalesRepList: any[] = [];

      rankByWgsDetailsRawData.sal_branch.forEach((element: any) => {
        tempSalesRepBranches.push({
          label: element,
          value: element,
        });
      });

      setSalesRepBranches(tempSalesRepBranches);

      let tempSelectedBranch = "";
      let tempSelectedSalesRep = selectedSalesRep?.SALESMAN;

      rankByWgsDetailsRawData.unique_salesman.Salesman.forEach(
        (element: any, index: any) => {
          if (element === selectedSalesRep?.SALESMAN) {
            tempSelectedBranch =
              rankByWgsDetailsRawData.unique_salesman.BRANCH[index];
            setOriginalBranch(tempSelectedBranch);
            setselectedSalesRepBranch({
              label: tempSelectedBranch,
              value: tempSelectedBranch,
            });

            setSelectedSalesRepSalesRep({
              label: element,
              value: element,
            });
          }
        }
      );

      if (tempSelectedBranch === "") {
        tempSelectedBranch = originalBranch;
      }
      rankByWgsDetailsRawData.unique_salesman.BRANCH.forEach(
        (element: any, index: any) => {
          if (element === tempSelectedBranch) {
            tempSalesRepList.push({
              label: rankByWgsDetailsRawData.unique_salesman.Salesman[index],
              value: rankByWgsDetailsRawData.unique_salesman.Salesman[index],
            });
          }
        }
      );

      setSalesRepList(tempSalesRepList);

      if (rankByWgsDetailsRawData.salesman_12_metric) {
        const sal12Filter = GetObject(
          GetArray(rankByWgsDetailsRawData?.salesman_12_metric).filter(
            (item: any) =>
              item.BRANCH ===
                (selectedSalesRepBranch
                  ? selectedSalesRepBranch?.value
                  : originalBranch) && item.SALESMAN === tempSelectedSalesRep
          )
        );

        const tempGainLostTotalData: any[] = [];
        if (sal12Filter.END_PERIOD) {
          sal12Filter?.END_PERIOD.forEach((element: any, index: any) => {
            tempGainLostTotalData.push({
              name: element,
              Gained: sal12Filter?.GAINED_ACCOUNT?.[index],
              Lost: sal12Filter?.LOST_ACCOUNT?.[index],
              Active: sal12Filter?.TOTAL_ACCOUNT?.[index],
            });
          });
        }

        setGainLostTotalData(tempGainLostTotalData);

        setSgain(
          sal12Filter.GAINED_ACCOUNT
            ? sal12Filter.GAINED_ACCOUNT[sal12Filter.GAINED_ACCOUNT.length - 1]
            : 0
        );
        setSlost(
          sal12Filter.LOST_ACCOUNT
            ? sal12Filter.LOST_ACCOUNT[sal12Filter.LOST_ACCOUNT.length - 1]
            : 0
        );
        setSacc(
          sal12Filter.TOTAL_ACCOUNT
            ? sal12Filter.TOTAL_ACCOUNT[sal12Filter.TOTAL_ACCOUNT.length - 1]
            : 0
        );
      }

      if (rankByWgsDetailsRawData.salesman_lead) {
        if (
          rankByWgsDetailsRawData.salesman_lead["Lead"][
            tempSelectedBranch
          ]?.hasOwnProperty(tempSelectedSalesRep)
        ) {
          setRetentionLeadTableData(
            rankByWgsDetailsRawData.salesman_lead["Lead"][tempSelectedBranch][
              tempSelectedSalesRep
            ]
          );
        } else {
          setRetentionLeadTableData([]);
        }
      }
    }
  };

  useEffect(() => {
    if (salemRawData || salstbRawData) salesRepFilterData();
  }, [salemRawData, salstbRawData, rankByWgsDetailsRawData]);

  useEffect(() => {
    if (rankByWgsDetailsRawData) salesRepFilterDetailData();
  }, [rankByWgsDetailsRawData]);

  useEffect(() => {
    if (clientSalesmenData) salesRepMetricsFilterData();
  }, [clientSalesmenData]);

  const userColumns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        id: "Table",
        header: "",
        columns: [
          {
            accessorKey: "Branch",
            header: "Branch",
          },
          {
            accessorKey: "Department",
            header: "Department",
          },
          {
            accessorKey: "Salesman",
            header: "Salesman",
          },
          {
            accessorKey: "Customer",
            header: "Customer",
          },
          {
            accessorKey: "Cust #",
            header: "Cust #",
          },
          {
            accessorKey: "Rev Rolling 12 months",
            header: "Rev Rolling 12 Months",
          },
          {
            accessorKey: "Rev Prior Rolling 12 months",
            header: "Rev Prior Rolling 12 months",
          },
          {
            accessorKey: "Order Frequency 12 months",
            header: "Order Frequency 12 months",
          },
          {
            accessorKey: "Order Frequency Prior 12 months",
            header: "Order Frequency Prior 12 months",
          },
          {
            accessorKey: "Risk Status",
            header: "Risk Status",
          },
          {
            accessorKey: "Last Purchase Date",
            header: "Last Purchase Date",
          },
          {
            accessorKey: "Contact",
            header: "Contact",
          },
          {
            accessorKey: "Phone",
            header: "Phone",
          },
          {
            accessorKey: "Email",
            header: "Email",
          },
        ],
      },
    ],
    []
  );

  const userTable = useMaterialReactTable({
    columns: userColumns,
    data: retentionLeadTableData ? retentionLeadTableData : [],
    state: {
      isLoading: rankByWgsDetailsLoading,
    },
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableColumnPinning: false,
    enableFacetedValues: true,
    enableRowActions: false,
    enableRowSelection: false,
    initialState: {
      showColumnFilters: true,
      showGlobalFilter: true,
      columnPinning: {
        left: ["mrt-row-expand", "mrt-row-select"],
        right: ["mrt-row-actions"],
      },
    },
    paginationDisplayMode: "pages",
    positionToolbarAlertBanner: "bottom",
    muiSearchTextFieldProps: {
      size: "small",
      variant: "outlined",
    },
    muiPaginationProps: {
      color: "secondary",
      rowsPerPageOptions: [5, 10, 20],
      shape: "rounded",
      variant: "outlined",
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          padding: "8px",
          flexWrap: "wrap",
        }}
      >
        <Button
          disabled={table.getPrePaginationRowModel().rows.length === 0}
          onClick={() =>
            exportToExcel(
              table.getPrePaginationRowModel().rows,
              "Sales Rep Ranking"
            )
          }
          startIcon={<FileDownloadIcon />}
        >
          Export All Rows
        </Button>
        <Button
          disabled={table.getRowModel().rows.length === 0}
          onClick={() =>
            exportToExcel(table.getRowModel().rows, "Sales Rep Ranking")
          }
          startIcon={<FileDownloadIcon />}
        >
          Export Page Rows
        </Button>
      </Box>
    ),
  });

  // View-level archetype change (same pattern as LegacySelectionView vs
  // DashboardChooser): "banner" keeps today's five-card layout verbatim below;
  // "plain" falls through to the approved Option B scoreboard layout.
  // Everything above this line is shared — one copy of the thunk dispatches,
  // handlers, MRT config and xlsx export serves both branches.
  if (tokens.cardChrome.variant === "banner") {
  return (
    <Grid container sx={{}}>
      {/* Title/last-updated come from the shell's ContextBar now; rendering
          DashboardHeader here too would stack a second header under it. */}
      <Grid
        container
        sx={{
          padding: "0px 20px 10px 20px",
        }}
      >
        <Grid container item xs={4} sx={{ padding: "0px 0px 0px 10px" }}>
          <FormControl fullWidth>
            <Autocomplete
              id="Branch"
              value={selectedSalesRepBranch}
              onChange={(_: SyntheticEvent, newValue: string | null) =>
                handleBranchChange(newValue)
              }
              options={salesRepBranches}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Branches"
                  placeholder="Branches"
                />
              )}
            />
          </FormControl>
        </Grid>
        <Grid container item xs={4} sx={{ padding: "0px 0px 0px 10px" }}>
          <FormControl fullWidth>
            <Autocomplete
              id="Sale Rep"
              value={selectedSalesRepSalesRep}
              onChange={(_: SyntheticEvent, newValue: string | null) => {
                handleSalesRepChange(newValue);
              }}
              options={salesRepList}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sale Rep"
                  placeholder="Sale Rep"
                />
              )}
            />
          </FormControl>
        </Grid>
      </Grid>
      <Grid
        container
        sx={{
          padding: "0px 20px 10px 20px",
        }}
      >
        <Grid container item xs={6} sx={{ padding: "10px" }}>
          <SectionCard title="Sales Rep Ranking by Stable Account %">
            <Grid
              container
              item
              xs={12}
              sx={{
                padding: "10px 20px 10px 20px",
                border: tokens.cardChrome.bodyBorder,
                backgroundColor: tokens.cardChrome.bodyBg,
                gap: 2,
              }}
            >
              <Grid
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "row",
                  gap: 1,
                }}
              >
                <Typography sx={{ fontWeight: "bold" }}>
                  Stable Account Targer &gt; 40%
                </Typography>
              </Grid>
              {rankByStableAccountLoading ? (
                <Grid
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "300px",
                          flexBasis: "300px",
                    border: tokens.cardChrome.bodyBorder,
                    width: "100%",
                  }}
                >
                  <MiniSpinner />
                </Grid>
              ) : (
                <Grid
                  container
                  item
                  xs={12}
                  sx={{ height: "300px", width: "100%" }}
                >
                  <BarChart
                    series={[
                      {
                        data: salstbChartData,
                        label: "Stable Account %",
                        color: tokens.chart.series.activeAccount,
                      },
                    ]}
                    yAxis={[
                      {
                        scaleType: "band",
                        data: salstbChartLabels,
                      },
                    ]}
                    slotProps={{
                      legend: {
                        direction: "row",
                        position: { vertical: "top", horizontal: "right" },
                        padding: 0,
                        itemMarkWidth: 15,
                        itemMarkHeight: 15,
                      },
                    }}
                    margin={{
                      left: 150,
                      right: 20,
                      top: 40,
                      bottom: 20,
                    }}
                    layout="horizontal"
                    grid={{ vertical: true, horizontal: true }}
                  />
                </Grid>
              )}
            </Grid>
          </SectionCard>
        </Grid>
        <Grid container item xs={6} sx={{ padding: "10px" }}>
          <SectionCard title="Sales Rep Ranking by Email %">
            <Grid
              container
              item
              xs={12}
              sx={{
                padding: "10px 20px 10px 20px",
                border: tokens.cardChrome.bodyBorder,
                backgroundColor: tokens.cardChrome.bodyBg,
                gap: 2,
              }}
            >
              <Grid>
                <Typography sx={{ fontWeight: "bold" }}>
                  Customer Email Percent Target &gt; 95%
                </Typography>{" "}
              </Grid>
              {rankByEmailLoading ? (
                <Grid
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "300px",
                          flexBasis: "300px",
                    border: tokens.cardChrome.bodyBorder,
                    width: "100%",
                  }}
                >
                  <MiniSpinner />
                </Grid>
              ) : (
                <Grid
                  container
                  item
                  xs={12}
                  sx={{ height: "300px", width: "100%" }}
                >
                  <BarChart
                    series={[
                      {
                        data: salemChartData,
                        label: "Email %",
                        color: tokens.chart.series.activeAccount,
                      },
                    ]}
                    yAxis={[
                      {
                        scaleType: "band",
                        data: salemChartLabels,
                      },
                    ]}
                    slotProps={{
                      legend: {
                        direction: "row",
                        position: { vertical: "top", horizontal: "right" },
                        padding: 0,
                        itemMarkWidth: 15,
                        itemMarkHeight: 15,
                      },
                    }}
                    margin={{
                      left: 150,
                      right: 20,
                      top: 40,
                      bottom: 20,
                    }}
                    layout="horizontal"
                    grid={{ vertical: true, horizontal: true }}
                  />
                </Grid>
              )}
            </Grid>
          </SectionCard>
        </Grid>
        <Grid container item xs={6} sx={{ padding: "10px" }}>
          <SectionCard title="Sales Rep Ranking Metrics">
            <Grid
              container
              item
              xs={12}
              sx={{
                padding: "10px 20px 10px 20px",
                border: tokens.cardChrome.bodyBorder,
                backgroundColor: tokens.cardChrome.bodyBg,
                gap: 2,
              }}
            >
              {clientSalesmenMetricsLoading ? (
                <Grid
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "300px",
                          flexBasis: "300px",
                    border: tokens.cardChrome.bodyBorder,
                    width: "100%",
                  }}
                >
                  <MiniSpinner />
                </Grid>
              ) : (
                <Grid
                  container
                  item
                  xs={12}
                  sx={{ height: "300px", width: "100%" }}
                >
                  <BarChart
                    xAxis={[
                      {
                        scaleType: "band",
                        data: clientSalesmenChartLabel,
                      },
                    ]}
                    series={[
                      {
                        data: clientSalesmenChartMetricAverage,
                        label: "Average",
                        color: tokens.chart.series.invoice,
                      },
                      {
                        data: clientSalesmenChartYou,
                        label: "You",
                        color: tokens.chart.series.activeAccount,
                      },
                    ]}
                  />
                </Grid>
              )}
            </Grid>
          </SectionCard>
        </Grid>
        <Grid container item xs={6} sx={{ padding: "10px" }}>
          <SectionCard title="Customer Gain & Lost">
            {rankByWgsDetailsLoading ? (
              <Grid
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "300px",
                          flexBasis: "300px",
                  border: tokens.cardChrome.bodyBorder,
                  width: "100%",
                }}
              >
                <MiniSpinner />
              </Grid>
            ) : (
              <Grid
                container
                item
                xs={12}
                sx={{
                  padding: "10px 20px 10px 20px",
                  border: tokens.cardChrome.bodyBorder,
                  backgroundColor: tokens.cardChrome.bodyBg,
                  gap: 2,
                }}
              >
                <Grid container item xs={12} sx={{}}>
                  <Grid container item xs={3} sx={{ padding: "5px" }}>
                    <Typography>12 Month : </Typography>
                  </Grid>
                  <Grid container item xs={3} sx={{ padding: "5px" }}>
                    Gained : {sgain}
                  </Grid>
                  <Grid container item xs={3} sx={{ padding: "5px" }}>
                    Lost : {slost}
                  </Grid>
                  <Grid container item xs={3} sx={{ padding: "5px" }}>
                    Active Account : {sacc}
                  </Grid>
                </Grid>

                <Grid container item xs={12} sx={{ height: "300px" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      width={500}
                      height={400}
                      data={gainLostTotalData}
                      margin={{
                        top: 10,
                        right: 20,
                        bottom: 60,
                        left: 0,
                      }}
                    >
                      <XAxis
                        dataKey="name"
                        scale="auto"
                        angle={-80}
                        tickMargin={25}
                        style={{
                          fontSize: "12px",
                        }}
                        interval={0}
                      />
                      <YAxis />
                      <Tooltip />
                      <Legend verticalAlign="top" />
                      <Bar dataKey="Active" fill={tokens.chart.gainLost.active} />
                      <Line dataKey="Lost" stroke={tokens.chart.gainLost.lost} />
                      <Line dataKey="Gained" stroke={tokens.chart.nps.promoters} /* byte-equal to the original rgba(0,161,77,1); salesRep's Gained green differs from growthForecast's */ />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Grid>
              </Grid>
            )}
          </SectionCard>
        </Grid>
      </Grid>
      <Grid
        container
        sx={{
          padding: "0px 20px 10px 20px",
        }}
      >
        <Grid container item xs={12} sx={{ padding: "10px" }}>
          <SectionCard title="Retention Lead">
          <Grid
            container
            item
            xs={12}
            sx={{
              padding: "10px 20px 10px 20px",
              border: tokens.cardChrome.bodyBorder,
              backgroundColor: tokens.cardChrome.bodyBg,
              gap: 2,
            }}
          >
            <Grid container sx={{}}>
              <Grid container item xs={4} sx={{}}>
                <FormControl fullWidth>
                  <Autocomplete
                    id="Lead"
                    value={selectedSalesRepRetLead}
                    onChange={(_: SyntheticEvent, newValue: any) => {
                      handleRetentionLeadChange(newValue);
                    }}
                    options={retentionLeadOption}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Lead"
                        placeholder="Lead"
                        error={retLeadError}
                        helperText={retLeadErrorMessage}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>
            <Grid container sx={{}}>
              {selectedSalesRepRetLead && (
                <TableTemplate tableData={userTable} />
              )}
            </Grid>
          </Grid>
          </SectionCard>
        </Grid>
      </Grid>
    </Grid>
  );
  }

  // ── Refreshed ("plain") layout derivations — pure reads of the same state
  //    the legacy layout renders, computed after its early return so that path
  //    pays nothing. No new dispatches, no new payload shapes.
  const glPoints: any[] = Array.isArray(gainLostTotalData) ? gainLostTotalData : [];
  const glLast = glPoints[glPoints.length - 1];
  const glPrior = glPoints[glPoints.length - 2];
  const glMonths = glPoints.map((p: any) => p.name);
  const glGained = glPoints.map((p: any) => Number(p.Gained ?? 0));
  // Lost plots negative so it hangs below the axis; x-charts' diverging stack
  // puts it under zero in the same column as Gained.
  const glLost = glPoints.map((p: any) => -Number(p.Lost ?? 0));
  const gainedDelta = glLast && glPrior ? pctChange(glLast.Gained, glPrior.Gained) : undefined;
  const activeDelta = glLast && glPrior ? pctChange(glLast.Active, glPrior.Active) : undefined;
  // StatTile's delta chip colours ">0 = good" (green up); for Lost that would
  // invert the meaning, so the change vs prior rides in the caption instead.
  const lostDiff =
    glLast && glPrior && glPrior.Lost != null
      ? Number(glLast.Lost) - Number(glPrior.Lost)
      : undefined;
  const lostCaption =
    lostDiff === undefined
      ? undefined
      : `${lostDiff > 0 ? "+" : ""}${lostDiff} vs prior period`;
  const net = Number(sgain) - Number(slost);

  // The payloads carry no gained/lost-per-rep ranking, so the side panel ranks
  // what exists — stable-account % and email % per rep — titled truthfully.
  const stableRanking = buildRankEntries(
    salstbChartLabels,
    salstbChartData,
    selectedSalesRepSalesRep?.value
  );
  const emailRanking = buildRankEntries(
    salemChartLabels,
    salemChartData,
    selectedSalesRepSalesRep?.value
  );

  const branchKey = selectedSalesRepBranch?.value;
  const repKey = selectedSalesRepSalesRep?.value;
  const bucketOptions = retentionLeadOption.map((opt: any) => {
    const rows =
      branchKey && repKey
        ? rankByWgsDetailsRawData?.salesman_lead?.[opt.value]?.[branchKey]?.[repKey]
        : undefined;
    return {
      value: opt.value as string,
      label: Array.isArray(rows) ? `${opt.label} · ${rows.length}` : opt.label,
    };
  });
  // salesRepFilterDetailData preloads the "Lead" bucket's rows, so an empty
  // selection is visually the Lead bucket rather than a blank gate.
  const activeBucket = selectedSalesRepRetLead?.value ?? "Lead";
  const activeBucketLabel =
    retentionLeadOption.find((o: any) => o.value === activeBucket)?.label ??
    activeBucket;
  const hasRows =
    Array.isArray(retentionLeadTableData) && retentionLeadTableData.length > 0;
  const handleBucketChange = (value: string) => {
    const opt = retentionLeadOption.find((o: any) => o.value === value);
    // Same option object shape the Lead Autocomplete passes — one handler.
    if (opt) handleRetentionLeadChange(opt);
  };

  return (
    <Box
      sx={{
        padding: { xs: "0 12px 24px", md: "0 20px 32px" },
        display: "flex",
        flexDirection: "column",
        gap: 2,
        // This Box is a flex item of dashboardView's <Grid container>. With
        // the default min-width:auto it sizes to the lead table's ~2640px
        // min-content, every sibling stretches to match, and the page scrolls
        // sideways (diagnosed live 2026-08-07). Pin it to the container so the
        // table's own container scrolls instead.
        minWidth: 0,
        width: "100%",
        maxWidth: "100%",
      }}
    >
      {/* Branch / rep pickers — same state and handlers the legacy Autocompletes drive */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Autocomplete
          id="Branch"
          size="small"
          sx={{ width: { xs: "100%", sm: 240 } }}
          value={selectedSalesRepBranch}
          onChange={(_: SyntheticEvent, newValue: string | null) =>
            handleBranchChange(newValue)
          }
          options={salesRepBranches}
          renderInput={(params) => (
            <TextField {...params} label="Branch" placeholder="Branch" />
          )}
        />
        <Autocomplete
          id="Sale Rep"
          size="small"
          sx={{ width: { xs: "100%", sm: 240 } }}
          value={selectedSalesRepSalesRep}
          onChange={(_: SyntheticEvent, newValue: string | null) =>
            handleSalesRepChange(newValue)
          }
          options={salesRepList}
          renderInput={(params) => (
            <TextField {...params} label="Sales rep" placeholder="Sales rep" />
          )}
        />
      </Box>

      {/* KPI strip — the same totals the legacy "Customer Gain & Lost" header shows */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0,1fr)",
            sm: "repeat(3, minmax(0,1fr))",
          },
          gap: 2,
        }}
      >
        <Box sx={KPI_CARD_SX}>
        <StatTile
          bg={tokens.color.surface}
          eyebrow="Accounts gained"
          value={sgain}
          label="Trailing 12 months"
          align="left"
          delta={gainedDelta}
          loading={rankByWgsDetailsLoading}
        />
        </Box>
        <Box sx={KPI_CARD_SX}>
        <StatTile
          bg={tokens.color.surface}
          eyebrow="Accounts lost"
          value={slost}
          label="Trailing 12 months"
          align="left"
          caption={lostCaption}
          loading={rankByWgsDetailsLoading}
        />
        </Box>
        <Box sx={KPI_CARD_SX}>
        <StatTile
          bg={tokens.color.surface}
          eyebrow="Active accounts"
          value={sacc}
          label="Trailing 12 months"
          align="left"
          delta={activeDelta}
          loading={rankByWgsDetailsLoading}
        />
        </Box>
      </Box>

      {/* Scoreboard: diverging gain/lost bars + the rankings side panel */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0,1fr)",
            lg: `minmax(0,1fr) ${RANKING_PANEL_WIDTH}px`,
          },
          gap: 2,
          alignItems: "stretch",
        }}
      >
        <SectionCard
          title="Gained & lost accounts"
          action={<Typography sx={{ ...eyebrowSx }}>12 month</Typography>}
        >
          {rankByWgsDetailsLoading ? (
            <ChartLoading height={320} />
          ) : glMonths.length === 0 ? (
            <EmptyState
              kind="empty"
              title="No gained & lost data"
              body="No gained/lost series came back for this branch and rep."
            />
          ) : (
            <>
              <Box sx={{ height: 320, width: "100%" }}>
                <BarChart
                  xAxis={[
                    {
                      scaleType: "band",
                      data: glMonths,
                      tickLabelStyle: {
                        fontSize: 10,
                        angle: -40,
                        textAnchor: "end",
                      },
                    },
                  ]}
                  series={[
                    {
                      data: glGained,
                      label: "Gained",
                      color: tokens.chart.gainLost.gained,
                      stack: "gainLost",
                      valueFormatter: absCount,
                    },
                    {
                      data: glLost,
                      label: "Lost",
                      color: tokens.chart.gainLost.lost,
                      stack: "gainLost",
                      valueFormatter: absCount,
                    },
                  ]}
                  slotProps={{
                    legend: {
                      direction: "row",
                      position: { vertical: "top", horizontal: "right" },
                      padding: 0,
                      itemMarkWidth: 12,
                      itemMarkHeight: 12,
                    },
                  }}
                  margin={{ left: 40, right: 12, top: 36, bottom: 56 }}
                  grid={{ horizontal: true }}
                />
              </Box>
              <Typography
                sx={{ fontSize: 12, color: tokens.color.faint, paddingTop: 1 }}
              >
                Gained above the axis, lost below · latest period nets{" "}
                {net >= 0 ? `+${net}` : net} accounts
              </Typography>
            </>
          )}
        </SectionCard>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <RankMiniList
            title="Stable account % — reps"
            entries={stableRanking}
            formatValue={(v) => `${v.toFixed(0)}%`}
            loading={rankByStableAccountLoading}
            emptyBody="No stable-account ranking for this branch yet."
          />
          <RankMiniList
            title="Email coverage % — reps"
            entries={emailRanking}
            formatValue={(v) => `${v.toFixed(0)}%`}
            loading={rankByEmailLoading}
            emptyBody="No email-coverage ranking for this branch yet."
          />
        </Box>
      </Box>

      {/* Retention leads — same MRT table; the bucket chips drive the same
          state the legacy Lead Autocomplete sets */}
      <SectionCard title="Retention leads">
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ overflowX: "auto", maxWidth: "100%" }}>
            <SegmentedControl
              options={bucketOptions}
              value={activeBucket}
              onChange={handleBucketChange}
            />
          </Box>
          {retLeadError && (
            <Typography
              sx={{ fontSize: 12.5, color: tokens.color.negative.text }}
            >
              {retLeadErrorMessage}
            </Typography>
          )}
          {hasRows || rankByWgsDetailsLoading ? (
            <TableTemplate tableData={userTable} />
          ) : (
            <EmptyState
              kind="empty"
              title="No accounts in this bucket"
              body={`${activeBucketLabel} has no accounts for the selected branch and rep.`}
            />
          )}
        </Box>
      </SectionCard>

      {/* Rankings detail — the full ranking charts and You-vs-Average metrics
          the side panel summarises, so nothing the legacy layout shows is lost */}
      <SectionCard title="Rankings detail">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0,1fr)",
              md: "repeat(2, minmax(0,1fr))",
            },
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: tokens.color.ink,
                paddingBottom: 1,
              }}
            >
              Stable account % — target &gt; 40%
            </Typography>
            {rankByStableAccountLoading ? (
              <ChartLoading />
            ) : salstbChartLabels.length === 0 ? (
              <EmptyState
                kind="empty"
                title="No ranking data"
                body="No stable-account ranking for this branch yet."
              />
            ) : (
              <Box sx={{ height: 300, width: "100%" }}>
                <BarChart
                  series={[
                    {
                      data: salstbChartData,
                      label: "Stable Account %",
                      color: tokens.chart.series.activeAccount,
                    },
                  ]}
                  yAxis={[{ scaleType: "band", data: salstbChartLabels }]}
                  slotProps={{
                    legend: {
                      direction: "row",
                      position: { vertical: "top", horizontal: "right" },
                      padding: 0,
                      itemMarkWidth: 12,
                      itemMarkHeight: 12,
                    },
                  }}
                  margin={{ left: 150, right: 20, top: 40, bottom: 20 }}
                  layout="horizontal"
                  grid={{ vertical: true, horizontal: true }}
                />
              </Box>
            )}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: tokens.color.ink,
                paddingBottom: 1,
              }}
            >
              Email % — target &gt; 95%
            </Typography>
            {rankByEmailLoading ? (
              <ChartLoading />
            ) : salemChartLabels.length === 0 ? (
              <EmptyState
                kind="empty"
                title="No ranking data"
                body="No email-coverage ranking for this branch yet."
              />
            ) : (
              <Box sx={{ height: 300, width: "100%" }}>
                <BarChart
                  series={[
                    {
                      data: salemChartData,
                      label: "Email %",
                      color: tokens.chart.series.activeAccount,
                    },
                  ]}
                  yAxis={[{ scaleType: "band", data: salemChartLabels }]}
                  slotProps={{
                    legend: {
                      direction: "row",
                      position: { vertical: "top", horizontal: "right" },
                      padding: 0,
                      itemMarkWidth: 12,
                      itemMarkHeight: 12,
                    },
                  }}
                  margin={{ left: 150, right: 20, top: 40, bottom: 20 }}
                  layout="horizontal"
                  grid={{ vertical: true, horizontal: true }}
                />
              </Box>
            )}
          </Box>
          <Box sx={{ minWidth: 0, gridColumn: { xs: "auto", md: "1 / -1" } }}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: tokens.color.ink,
                paddingBottom: 1,
              }}
            >
              You vs average — key metrics
            </Typography>
            {clientSalesmenMetricsLoading ? (
              <ChartLoading />
            ) : clientSalesmenChartLabel.length === 0 ? (
              <EmptyState
                kind="empty"
                title="No metric data"
                body="No rep-vs-average metrics came back for this rep."
              />
            ) : (
              <Box sx={{ height: 300, width: "100%" }}>
                <BarChart
                  xAxis={[
                    { scaleType: "band", data: clientSalesmenChartLabel },
                  ]}
                  series={[
                    {
                      data: clientSalesmenChartMetricAverage,
                      label: "Average",
                      color: tokens.chart.series.invoice,
                    },
                    {
                      data: clientSalesmenChartYou,
                      label: "You",
                      color: tokens.chart.series.activeAccount,
                    },
                  ]}
                />
              </Box>
            )}
          </Box>
        </Box>
      </SectionCard>
    </Box>
  );
};

export default SalesRepView;
